import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

const aboutSEO = {
  title: '关于蘑菇视频 - 品牌介绍 | 蘑菇视频官网',
  description: '蘑菇视频是官方正版视频平台，提供最新影视、综艺、短视频内容。了解蘑菇视频的品牌故事、平台优势和联系方式，在蘑菇视频官网畅享极致视频体验。',
  keywords: '关于蘑菇视频,蘑菇视频品牌介绍,蘑菇视频平台优势,蘑菇视频联系方式,蘑菇视频官网',
  canonicalUrl: '/about',
};

const advantages = [
  {
    icon: 'ri-shield-check-line',
    title: '正版内容',
    description: '所有内容均获得正版授权，安全合法观看，支持原创内容创作者，为用户提供优质的正版视频体验。',
  },
  {
    icon: 'ri-4k-line',
    title: '超清画质',
    description: '支持最高4K HDR超清画质播放，采用先进的视频编码技术，根据网络状况智能调节，确保流畅观看体验。',
  },
  {
    icon: 'ri-device-line',
    title: '多端同步',
    description: '支持手机、平板、电脑、智能电视等多终端无缝切换，播放记录云端同步，随时随地继续观看。',
  },
  {
    icon: 'ri-download-cloud-2-line',
    title: '离线下载',
    description: '会员用户支持视频离线下载功能，无网络环境下也能畅享精彩内容，通勤出行不再无聊。',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: '智能推荐',
    description: '基于人工智能算法，根据用户的观看历史和偏好，精准推荐个性化内容，发现更多感兴趣的视频。',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: '全天客服',
    description: '提供7×24小时在线客服支持，快速响应用户问题，用心服务每一位蘑菇视频的用户。',
  },
];

const milestones = [
  { year: '2018', event: '蘑菇视频项目正式启动，创始团队致力于打造新一代视频平台' },
  { year: '2019', event: '完成首轮融资，用户数突破100万，上线移动端App' },
  { year: '2020', event: '推出会员服务体系，日活用户突破500万，内容库扩展至10万+' },
  { year: '2021', event: '智能推荐系统上线，获评年度最佳视频平台，用户数突破2000万' },
  { year: '2022', event: '推出4K超清画质，与多家顶级内容方达成战略合作，日活用户突破1000万' },
  { year: '2023', event: '国际化战略启动，上线多语言版本，全球累计用户超过5000万' },
  { year: '2024', event: 'AI智能剪辑功能发布，获多项行业大奖，成为国内领先的视频平台之一' },
  { year: '2025', event: '推出创作者激励计划，平台生态进一步完善，用户日均使用时长超90分钟' },
  { year: '2026', event: '全面升级技术架构，日活用户突破5000万，持续引领行业创新发展' },
];

export default function AboutPage() {
  return (
    <>
      <SEOHead {...aboutSEO} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb />

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 mb-6">
                <i className="ri-information-line text-primary-600 text-sm w-4 h-4 flex items-center justify-center"></i>
                <span className="text-primary-700 text-xs font-medium whitespace-nowrap">关于蘑菇视频</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-4">
                让每个人都享受视频的乐趣
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                蘑菇视频成立于2018年，是一家专注于视频内容聚合与分发的互联网科技公司。
                我们致力于通过技术创新，让每一位用户都能轻松发现、观看和分享优质视频内容，
                打造一个开放、多元、充满活力的视频社区平台。
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 mb-16">
              {[
                { value: '5000万+', label: '日活用户' },
                { value: '100万+', label: '视频内容' },
                { value: '8年', label: '行业深耕' },
                { value: '500+', label: '团队成员' },
                { value: '98%', label: '用户满意度' },
                { value: '200+', label: '合作伙伴' },
              ].map((stat) => (
                <div key={stat.label} className="text-center bg-background-100 rounded-xl p-5">
                  <div className="text-xl md:text-2xl font-heading text-primary-600 mb-1">{stat.value}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                平台优势
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                六大核心优势，为您提供极致的视频观看体验
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {advantages.map((adv) => (
                <div key={adv.title} className="bg-background-50 rounded-xl p-6 hover:shadow-sm transition-all duration-200">
                  <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center mb-4">
                    <i className={`${adv.icon} text-accent-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{adv.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{adv.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                发展历程
              </h2>
              <p className="text-sm text-foreground-600">
                从创业团队到行业领先，蘑菇视频的每一步都坚实有力
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px" />
              <div className="space-y-6">
                {milestones.map((m, i) => (
                  <div key={m.year} className={`relative pl-12 md:pl-0 ${i % 2 === 0 ? 'md:pr-[50%] md:text-right md:pr-12' : 'md:pl-[50%] md:pl-12'}`}>
                    <div className="absolute left-0 md:left-1/2 top-2 w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center -translate-x-1/2 border-4 border-background-50">
                      <div className="w-2 h-2 rounded-full bg-background-50" />
                    </div>
                    <div className="bg-background-100 rounded-xl p-5">
                      <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-2 whitespace-nowrap">
                        {m.year}
                      </span>
                      <p className="text-sm text-foreground-700 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                如果您有任何问题或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@mogutv.com', desc: '商务合作与媒体咨询' },
                { icon: 'ri-phone-line', title: '客服热线', value: '400-888-8888', desc: '7×24小时在线服务' },
                { icon: 'ri-map-pin-line', title: '公司地址', value: '北京市朝阳区望京科技园', desc: '蘑菇视频总部大楼' },
              ].map((contact) => (
                <div key={contact.title} className="bg-background-50 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${contact.icon} text-primary-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">{contact.title}</h3>
                  <p className="text-sm text-primary-600 font-medium mb-1">{contact.value}</p>
                  <p className="text-xs text-foreground-500">{contact.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}